<template>
  <v-stepper-window-item value="3">
    <!-- Update Confirmation Dialog -->
    <v-dialog v-model="confirmDialog" max-width="500">
      <v-card>
        <v-card-title class="text-h6 px-4 pt-4">
          Update Profiles
        </v-card-title>

        <v-card-text>
          <div class="d-flex justify-space-around mb-4">
            <v-chip color="green" label>
              Profiles to Update: {{ profilesWithChanges.length }}
            </v-chip>
            <v-chip color="grey" label>
              No Changes: {{ profilesWithoutChanges.length }}
            </v-chip>
          </div>

          <v-alert
            v-if="profilesToCreate.length > 0"
            type="warning"
            density="compact"
            class="mb-4"
          >
            {{ profilesToCreate.length }} profile(s) not found in system (will be skipped)
          </v-alert>

          <p class="text-body-2">
            This will update {{ profilesWithChanges.length }} profile(s) with the changes shown.
            Are you sure you want to continue?
          </p>
        </v-card-text>

        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn color="grey" @click="confirmDialog = false">
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            :loading="updating"
            @click="handleConfirm"
          >
            Confirm Update
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Change Detail Dialog -->
    <v-dialog v-model="changesDialog" max-width="700">
      <v-card v-if="selectedItem">
        <v-card-title class="text-h6 px-4 pt-4">
          Changes for: {{ selectedItem.name }}
        </v-card-title>

        <v-card-text>
          <v-list v-if="selectedItem.changeDetails?.length > 0">
            <v-list-subheader>Change Details</v-list-subheader>
            <v-list-item
              v-for="(detail, index) in selectedItem.changeDetails"
              :key="index"
            >
              <v-list-item-title>{{ detail }}</v-list-item-title>
            </v-list-item>
          </v-list>
          <div v-else class="text-body-2 text-medium-emphasis">
            No specific changes to display.
          </div>
        </v-card-text>

        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn color="primary" @click="changesDialog = false">
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Subtasks Dialog -->
    <SubtasksDialog
      v-model="subtasksDialog"
      :task="selectedTaskForSubtasks"
    />

    <v-card>
      <v-card-text class="pa-0">
        <!-- Errors/Warnings Alerts -->
        <v-alert
          v-if="importResult?.errors?.length"
          type="error"
          class="ma-4 mb-0"
        >
          <div class="font-weight-bold mb-2">Errors (must fix):</div>
          <div v-for="(err, idx) in importResult.errors" :key="idx" class="text-body-2">
            <template v-if="err.sheet">{{ err.sheet }}</template>
            <template v-if="err.row"> Row {{ err.row }}</template>
            <template v-if="err.column"> [{{ err.column }}]</template>
            : {{ err.message }}
          </div>
        </v-alert>

        <v-alert
          v-if="importResult?.warnings?.length"
          type="warning"
          class="ma-4 mb-0"
        >
          <div class="font-weight-bold mb-2">Warnings:</div>
          <div v-for="(warn, idx) in importResult.warnings" :key="idx" class="text-body-2">
            <template v-if="warn.sheet">{{ warn.sheet }}</template>
            <template v-if="warn.row"> Row {{ warn.row }}</template>
            : {{ warn.message }}
          </div>
        </v-alert>

        <!-- Profile Selector -->
        <div class="pa-4">
          <v-select
            v-model="selectedProfileCode"
            :items="profileSelectItems"
            item-title="title"
            item-value="code"
            label="Select Profile to Review"
            variant="outlined"
            density="comfortable"
            hide-details
          >
            <template #item="{ props: itemProps, item }">
              <v-list-item v-bind="itemProps">
                <template #append>
                  <v-chip
                    color="warning"
                    size="small"
                    label
                  >
                    {{ item.raw.changeCount }} change{{ item.raw.changeCount !== 1 ? 's' : '' }}
                  </v-chip>
                </template>
              </v-list-item>
            </template>
            <template #selection="{ item }">
              <span>{{ item.raw.title }}</span>
              <v-chip
                color="warning"
                size="small"
                label
                class="ml-2"
              >
                {{ item.raw.changeCount }} change{{ item.raw.changeCount !== 1 ? 's' : '' }}
              </v-chip>
            </template>
          </v-select>
        </div>

        <!-- Toolbar -->
        <v-toolbar color="primary" density="compact">
          <v-text-field
            v-model="searchQuery"
            label="Search"
            prepend-inner-icon="mdi-magnify"
            clearable
            density="compact"
            hide-details
            class="mx-2"
            style="max-width: 300px;"
          ></v-text-field>

          <v-switch
            v-model="showChangesOnly"
            label="Show Changes Only"
            density="compact"
            hide-details
            class="mx-2"
          ></v-switch>

          <v-spacer></v-spacer>

          <v-btn icon class="mx-1" title="Expand All" @click="expandAll">
            <v-icon>mdi-unfold-more-horizontal</v-icon>
          </v-btn>

          <v-btn icon class="mx-1" title="Collapse All" @click="collapseAll">
            <v-icon>mdi-unfold-less-horizontal</v-icon>
          </v-btn>
        </v-toolbar>

        <!-- Bryntum TreeGrid Container -->
        <div ref="gridContainer" class="tsp-tree-grid" style="height: 500px; min-height: 400px;"></div>
      </v-card-text>

      <v-card-actions>
        <v-btn @click="emit('back')">
          Back
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn
          color="primary"
          :disabled="!canUpdate"
          @click="openConfirmDialog"
        >
          Update {{ profilesWithChanges.length }} Profile(s)
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-stepper-window-item>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue';
import { useTaskStore } from '@/stores/task';
import { TreeGrid } from '@bryntum/gantt';
import {
  analyzeProfiles,
  normalizeValue,
  CHANGE_TYPES
} from '@/utils/TaskSetProfileChangeDetector';
import SubtasksDialog from '@components/dialogs/SubtasksDialog.vue';

import '@bryntum/gantt/gantt.stockholm.css';

const taskStore = useTaskStore();

const props = defineProps({
  importResult: {
    type: Object,
    default: null
  },
  existingProfiles: {
    type: Array,
    default: () => []
  },
  teams: {
    type: Array,
    default: () => []
  },
  updating: {
    type: Boolean,
    default: false
  }
});

// Lookup maps for resolving names
const templateMap = computed(() => {
  const map = new Map();
  for (const template of taskStore.itemTemplates || []) {
    map.set(template.code, template.title);
  }
  return map;
});

const teamMap = computed(() => {
  const map = new Map();
  for (const team of props.teams) {
    map.set(team.id, team.name);
  }
  return map;
});

const emit = defineEmits(['back', 'confirm']);

// Refs
const gridContainer = ref(null);
let treeGrid = null;

// State
const searchQuery = ref('');
const showChangesOnly = ref(false);
const confirmDialog = ref(false);
const changesDialog = ref(false);
const subtasksDialog = ref(false);
const selectedItem = ref(null);
const selectedTaskForSubtasks = ref(null);
const selectedProfileCode = ref(null);
const expandedIds = ref(new Set());

// Store subtask data separately from TreeGrid to avoid Bryntum data serialization issues
const subtaskDataMap = ref(new Map());

// Computed - Analyze profiles for changes
const analysisResult = computed(() => {
  if (!props.importResult?.profiles) {
    return {
      analyzedProfiles: [],
      profilesWithChanges: [],
      profilesWithoutChanges: [],
      profilesToCreate: []
    };
  }
  return analyzeProfiles(props.importResult.profiles, props.existingProfiles);
});

const profilesWithChanges = computed(() => analysisResult.value.profilesWithChanges);
const profilesWithoutChanges = computed(() => analysisResult.value.profilesWithoutChanges);
const profilesToCreate = computed(() => analysisResult.value.profilesToCreate);

const canUpdate = computed(() => {
  return props.importResult?.isValid && profilesWithChanges.value.length > 0;
});

// Computed - Items for profile selector dropdown (only profiles with changes)
const profileSelectItems = computed(() => {
  return profilesWithChanges.value.map(item => {
    const changeCount = item.changes.details?.length || 0;
    return {
      code: item.profile.code,
      title: `${item.profile.code} | ${item.profile.title}`,
      changeCount
    };
  });
});

// Computed - Get the selected profile's analyzed data
const selectedProfileData = computed(() => {
  if (!selectedProfileCode.value) return null;
  return analysisResult.value.analyzedProfiles.find(
    item => item.profile.code === selectedProfileCode.value
  );
});

function transformTasksToTreeData(importedTasks, existingTasks, profileCode) {
  // Build a map of existing tasks by versionReference
  const existingMap = new Map();
  flattenTasksWithParent(existingTasks).forEach(t => {
    existingMap.set(t.versionReference, t);
  });

  return importedTasks.map((task, index) => {
    const existingTask = existingMap.get(task.versionReference);
    let changeType = CHANGE_TYPES.UNCHANGED;
    const changeDetails = [];

    if (!existingTask) {
      changeType = CHANGE_TYPES.ADDED;
      changeDetails.push('New task added');
    } else {
      // Check for modifications
      if (normalizeValue(task.title) !== normalizeValue(existingTask.title)) {
        changeDetails.push(`Title: "${existingTask.title || ''}" → "${task.title || ''}"`);
      }
      if (normalizeValue(task.code) !== normalizeValue(existingTask.code)) {
        changeDetails.push(`Code: "${existingTask.code || '(empty)'}" → "${task.code || '(empty)'}"`);
      }
      if (changeDetails.length > 0) {
        changeType = CHANGE_TYPES.MODIFIED;
      }
    }

    // Use profile code + versionReference + index to ensure uniqueness
    const taskId = `${profileCode}-task-${task.versionReference || index}-${index}`;

    const result = {
      id: taskId,
      name: task.title,
      code: task.code || '',
      type: 'task',
      changeType,
      changeDetails,
      changeCount: changeDetails.length,
      expanded: changeType !== CHANGE_TYPES.UNCHANGED
    };

    // Add children (nested tasks)
    if (task.children?.length > 0) {
      result.children = transformTasksToTreeData(
        task.children,
        existingTask?.children || [],
        profileCode
      );
    }

    // Store subtasks in a separate Map (to avoid Bryntum data serialization issues)
    // and store counts in the tree data for display
    if (task.subtaskProfiles?.length > 0) {
      const subtasks = transformSubtasksForDialog(
        task.subtaskProfiles,
        existingTask?.subtaskProfiles || []
      );
      // Store in Map keyed by task ID
      subtaskDataMap.value.set(taskId, subtasks);
      result.subtaskCount = subtasks.length;
      result.subtaskChanges = subtasks.filter(
        s => s.changeType !== CHANGE_TYPES.UNCHANGED
      ).length;
    } else {
      subtaskDataMap.value.set(taskId, []);
      result.subtaskCount = 0;
      result.subtaskChanges = 0;
    }

    return result;
  });
}

function transformSubtasksForDialog(importedSubtasks, existingSubtasks) {
  console.log('transformSubtasksForDialog - importedSubtasks:', JSON.stringify(importedSubtasks, null, 2));
  const existingMap = new Map(existingSubtasks.map(s => [s.versionReference, s]));

  return importedSubtasks.map((subtask) => {
    const existingSubtask = existingMap.get(subtask.versionReference);
    let changeType = CHANGE_TYPES.UNCHANGED;
    const changeDetails = [];

    if (!existingSubtask) {
      changeType = CHANGE_TYPES.ADDED;
      changeDetails.push('New subtask added');
    } else {
      if (normalizeValue(subtask.title) !== normalizeValue(existingSubtask.title)) {
        changeDetails.push(`Title: "${existingSubtask.title || ''}" → "${subtask.title || ''}"`);
      }
      if (normalizeValue(subtask.defaultLeadByTeamId) !== normalizeValue(existingSubtask.defaultLeadByTeamId)) {
        const oldTeam = teamMap.value.get(existingSubtask.defaultLeadByTeamId) || existingSubtask.defaultLeadByTeamId || '(none)';
        const newTeam = teamMap.value.get(subtask.defaultLeadByTeamId) || subtask.defaultLeadByTeamId || '(none)';
        changeDetails.push(`Lead team: ${oldTeam} → ${newTeam}`);
      }
      if (normalizeValue(subtask.weighting) !== normalizeValue(existingSubtask.weighting)) {
        changeDetails.push(`Weighting: ${existingSubtask.weighting ?? '(none)'} → ${subtask.weighting ?? '(none)'}`);
      }
      if (normalizeValue(subtask.itemTemplateCode) !== normalizeValue(existingSubtask.itemTemplateCode)) {
        const oldTemplate = existingSubtask.itemTemplateCode || '(none)';
        const newTemplate = subtask.itemTemplateCode || '(none)';
        changeDetails.push(`Template: ${oldTemplate} → ${newTemplate}`);
      }
      if (changeDetails.length > 0) {
        changeType = CHANGE_TYPES.MODIFIED;
      }
    }

    const result = {
      title: subtask.title,
      versionReference: subtask.versionReference,
      weighting: subtask.weighting,
      itemTemplateCode: subtask.itemTemplateCode,
      itemTemplateTitle: templateMap.value.get(subtask.itemTemplateCode) || null,
      existingTemplateCode: existingSubtask?.itemTemplateCode || null,
      existingTemplateTitle: templateMap.value.get(existingSubtask?.itemTemplateCode) || null,
      defaultLeadByTeamId: subtask.defaultLeadByTeamId,
      leadTeamName: teamMap.value.get(subtask.defaultLeadByTeamId) || null,
      changeType,
      changeDetails
    };
    console.log('transformSubtasksForDialog - subtask result:', {
      title: result.title,
      itemTemplateCode: result.itemTemplateCode,
      changeDetails: result.changeDetails
    });
    return result;
  });
}

function flattenTasksWithParent(tasks, result = []) {
  for (const task of tasks) {
    result.push(task);
    if (task.children?.length > 0) {
      flattenTasksWithParent(task.children, result);
    }
  }
  return result;
}

// Transform selected profile to tree data (tasks only, no profile wrapper)
function transformSelectedProfileToTreeData() {
  if (!selectedProfileData.value) return [];

  const item = selectedProfileData.value;
  const profileCode = item.profile.code;

  return transformTasksToTreeData(
    item.profile.taskProfiles || [],
    item.existing?.taskProfiles || [],
    profileCode
  );
}

// Colors matching SnagR-Web pattern (using Vuetify colors)
const colors = {
  green: { lighten4: '#C8E6C9' },
  yellow: { lighten4: '#FFF9C4' },
  red: { lighten4: '#FFCDD2' },
  grey: { lighten2: '#E0E0E0', darken2: '#616161' }
};

// Initialize TreeGrid
function initTreeGrid() {
  if (!gridContainer.value) return;

  // Clear the subtask data map before building tree data
  subtaskDataMap.value.clear();
  const treeData = transformSelectedProfileToTreeData();

  treeGrid = new TreeGrid({
    appendTo: gridContainer.value,
    columns: [
      {
        field: 'code',
        text: 'Code',
        sortable: false,
        width: 120
      },
      {
        type: 'tree',
        field: 'name',
        text: 'Title',
        sortable: false,
        flex: 4,
        renderer: ({ row, record }) => {
          // Apply background colors based on change type (matching SnagR-Web pattern)
          row.eachElement(el => {
            if (record.changeType) {
              switch (record.changeType) {
                case CHANGE_TYPES.ADDED:
                  el.style.background = colors.green.lighten4;
                  break;
                case CHANGE_TYPES.MODIFIED:
                  el.style.background = colors.yellow.lighten4;
                  break;
                case CHANGE_TYPES.REMOVED:
                  el.style.background = colors.red.lighten4;
                  break;
                default:
                  el.style.removeProperty('background');
              }
            } else {
              el.style.removeProperty('background');
            }
          });
          return record.name || '';
        }
      },
      {
        field: 'changeCount',
        text: 'Changes',
        type: 'template',
        sortable: false,
        width: 100,
        align: 'center',
        template: ({ record }) => {
          const color = getChipBackgroundColor(record.changeType);
          const count = record.changeCount || 0;
          return `<div class="gantt-chip" style="background-color: ${color}; cursor: ${count > 0 ? 'pointer' : 'default'};">${count}</div>`;
        }
      },
      {
        field: 'subtaskCount',
        text: 'Subtasks',
        type: 'template',
        sortable: false,
        width: 120,
        align: 'center',
        template: ({ record }) => {
          const count = record.subtaskCount || 0;
          const changes = record.subtaskChanges || 0;
          if (count === 0) {
            return '';
          }
          const hasChanges = changes > 0;
          const chipColor = hasChanges ? '#ff9800' : '#9e9e9e';
          const label = hasChanges
            ? `${count} (${changes} changed)`
            : `${count} subtask${count !== 1 ? 's' : ''}`;
          return `<div class="subtask-chip" style="background-color: ${chipColor}; cursor: pointer;">${label}</div>`;
        }
      }
    ],
    features: {
      cellMenu: {
        items: {
          removeRow: false
        }
      }
    },
    store: {
      tree: true,
      data: treeData
    },
    listeners: {
      cellClick: ({ record, column }) => {
        if (column.field === 'changeCount' && record.changeCount > 0) {
          showChanges(record);
        }
        if (column.field === 'subtaskCount' && record.subtaskCount > 0) {
          showSubtasks(record);
        }
      },
      expandNode: ({ record }) => {
        expandedIds.value.add(record.id);
      },
      collapseNode: ({ record }) => {
        expandedIds.value.delete(record.id);
      }
    }
  });
}

function getChipBackgroundColor(changeType) {
  switch (changeType) {
    case CHANGE_TYPES.ADDED:
      return '#8bc34a'; // green
    case CHANGE_TYPES.MODIFIED:
      return '#ff9800'; // orange
    case CHANGE_TYPES.REMOVED:
      return '#c70000'; // red
    default:
      return '#3ca3f4'; // blue - unchanged
  }
}

function showChanges(record) {
  selectedItem.value = {
    name: record.name,
    type: record.type,
    changeType: record.changeType,
    changeDetails: record.changeDetails || []
  };
  changesDialog.value = true;
}

function showSubtasks(record) {
  // Retrieve subtask data from our Map (not from Bryntum record which may lose nested data)
  const subtasks = subtaskDataMap.value.get(record.id) || [];
  console.log('showSubtasks - record.id:', record.id);
  console.log('showSubtasks - subtaskDataMap keys:', [...subtaskDataMap.value.keys()]);
  console.log('showSubtasks - subtasks from map:', JSON.stringify(subtasks, null, 2));
  selectedTaskForSubtasks.value = {
    name: record.name,
    subtaskProfiles: subtasks
  };
  subtasksDialog.value = true;
}

function expandAll() {
  if (treeGrid) {
    treeGrid.expandAll();
  }
}

function collapseAll() {
  if (treeGrid) {
    treeGrid.collapseAll();
  }
}

function filterTasks() {
  if (!treeGrid) return;

  const searchFilter = (record) =>
    !searchQuery.value ||
    record.name?.toLowerCase().includes(searchQuery.value.toLowerCase()) ||
    record.code?.toLowerCase().includes(searchQuery.value.toLowerCase());

  const changesFilter = (record) =>
    !showChangesOnly.value || record.changeType !== CHANGE_TYPES.UNCHANGED;

  treeGrid.store.filter(record => searchFilter(record) && changesFilter(record));
}

function openConfirmDialog() {
  confirmDialog.value = true;
}

function handleConfirm() {
  const profilesToUpdate = profilesWithChanges.value.map(item => item.profile);
  emit('confirm', profilesToUpdate);
  confirmDialog.value = false;
}

// Auto-select first profile
function selectInitialProfile() {
  if (profileSelectItems.value.length === 0) {
    selectedProfileCode.value = null;
    return;
  }
  selectedProfileCode.value = profileSelectItems.value[0].code;
}

// Update tree when selection changes
function updateTreeData() {
  if (!treeGrid) return;

  // Clear the subtask data map before rebuilding
  subtaskDataMap.value.clear();
  const treeData = transformSelectedProfileToTreeData();
  treeGrid.store.data = treeData;
  filterTasks();
}

// Watchers
watch([searchQuery, showChangesOnly], () => {
  filterTasks();
});

watch(selectedProfileCode, () => {
  updateTreeData();
});

watch(() => props.importResult, () => {
  if (props.importResult) {
    selectInitialProfile();
    if (treeGrid) {
      updateTreeData();
    }
  }
}, { deep: true });

onMounted(() => {
  nextTick(() => {
    selectInitialProfile();
    initTreeGrid();
  });
});

onUnmounted(() => {
  if (treeGrid) {
    treeGrid.destroy();
    treeGrid = null;
  }
});
</script>

<style lang="scss" scoped>
/* Bryntum TreeGrid styling matching SnagR-Web pattern */
.tsp-tree-grid {
  :deep(.b-icon:not(.b-mask-icon)) {
    color: rgba(0, 0, 0, 0.54);
  }

  :deep(.b-grid-header-container) {
    .b-header {
      box-shadow: none;
    }

    .b-grid-header-scroller.b-header {
      border-bottom: 1px solid #E0E0E0;

      .b-grid-header {
        background-color: #FFFFFF;
      }
    }
  }

  :deep(.b-grid-cell) {
    font-weight: normal;
    color: #000000;
  }

  :deep(.b-tree-parent-row .b-grid-cell) {
    color: #616161;
  }

  :deep(.b-grid-row.b-tree-parent-row.b-tree-expanded) {
    .b-grid-cell:first-child {
      text-overflow: ellipsis;
      display: block;
      line-height: 45px;
    }
  }

  :deep(.b-editor.b-cell-editor) {
    border-color: transparent;
  }

  :deep(.b-using-keyboard) {
    .b-grid-cell, .b-grid-header {
      &:focus, &:focus-within {
        outline: 2px solid #0277BD;
      }
    }
  }

  /* Change count chip - matching ReviewStructureStep style */
  :deep(.gantt-chip) {
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 1em;
    margin-inline-start: 0.5em;
    padding: 0.3em 1em;
    color: #fff;
    font-size: 12px;
    font-weight: 600;
  }

  /* Subtask count chip */
  :deep(.subtask-chip) {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 1em;
    padding: 0.3em 0.8em;
    color: #fff;
    font-size: 11px;
    font-weight: 500;
    white-space: nowrap;
  }

  /* Warning/Error icons matching SnagR-Web pattern */
  :deep(.b-icon-warning:before) {
    content: 'warning';
    font-family: 'Material Icons';
    font-weight: 500;
    font-size: 16px;
    font-style: normal;
    line-height: 20px;
    color: #FFC107;
  }

  :deep(.b-icon-error:before) {
    content: 'error';
    font-family: 'Material Icons';
    font-weight: 500;
    font-size: 16px;
    font-style: normal;
    line-height: 20px;
    color: #F44336;
  }
}
</style>
